import { debounce } from 'lodash';
import { useCallback, useMemo } from 'react';
import { Control, useFieldArray, useFormContext } from 'react-hook-form';

import { CostCenter, useAllCostCenters } from '~/data/costCenters';
import { EmpPosition } from '~/data/employee';
import { useCostCenterListCustomFields } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/components/FormComponent/components/CostCenterComponent/CostCentersListComponent/useCostCenterListCustomFields';
import { ScheduleEmploymentFormCostCenterDetails } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/components/ScheduleEmployment/useScheduleEmployment';
import { Form } from '~/features/ScheduledEmploymentChanges/ScheduleChanges/useScheduleChanges';
import { addToTotal } from '~/utils/helper';

export type UseAdditionalCostCentersProps = {
  index: number;
  control: Control<Form>;
  selectedCostCenterId?: number;
  costCenterOptions: CostCenter[];
  empPositionPending?: EmpPosition;
  onListUpdate: () => void;
};

export const useCostCentersList = (props: UseAdditionalCostCentersProps) => {
  const {
    index,
    control,
    selectedCostCenterId,
    costCenterOptions,
    empPositionPending,
    onListUpdate,
  } = props;

  const { costCenters } = useAllCostCenters();
  const getCostCenterName = useCallback(
    (costCenterId: number) => {
      return costCenters.find((cc) => cc.fCostCenterId === costCenterId)
        ?.fCostCenterName;
    },
    [costCenters],
  );

  const { setValue, getValues } = useFormContext<Form>();
  const recalculateTotal = useMemo(
    () =>
      debounce(() => {
        const costCenterDetails = getValues(
          `schedule.${index}.empPosition.costCenterDetails`,
        );

        const total = costCenterDetails.reduce((total: number, cc) => {
          return addToTotal(total, cc.percent);
        }, 0);

        setValue(
          `schedule.${index}.empPosition.totalCostCenterPercentage`,
          total,
          {
            shouldDirty: true,
            shouldValidate: true,
          },
        );
      }, 600),
    [index, getValues, setValue],
  );

  const fieldArray = useFieldArray({
    control,
    name: `schedule.${index}.empPosition.costCenterDetails` as 'schedule.0.empPosition.costCenterDetails',
  });

  const { fields, append } = fieldArray;

  const onAddCostCenterRow = useCallback(
    (selectedCostCenter: CostCenter) => {
      const { fCostCenterId } = selectedCostCenter;

      const newRow: ScheduleEmploymentFormCostCenterDetails = {
        costCenterId: fCostCenterId,
        percent: undefined,
        customData: {},
        isDefault: false,
      };

      append(newRow);
      onListUpdate();
    },
    [append, onListUpdate],
  );

  const { disabledCostCenterOptions } = useMemo(() => {
    const selectedCostCenter = costCenterOptions.find(
      (cc) => cc.fCostCenterId !== selectedCostCenterId,
    );

    const additionalCostCenterIds: number[] = fields.map(
      (row) => row.costCenterId,
    );

    const additionalCostCenterOptions = costCenterOptions.filter(
      (cc) => cc.fCostCenterId !== selectedCostCenterId,
    );

    const disabledCostCenterOptions = costCenterOptions.filter(
      (cc) =>
        cc.fCostCenterId === selectedCostCenterId ||
        additionalCostCenterIds.includes(cc.fCostCenterId),
    );

    return {
      selectedCostCenter,
      additionalCostCenterOptions,
      disabledCostCenterOptions,
    };
  }, [selectedCostCenterId, costCenterOptions, fields]);

  const { customFields, isLoading } = useCostCenterListCustomFields({
    index,
    costCenterFields: fields,
    empPositionPending,
  });

  return {
    isLoading,
    index,
    control,
    costCenterOptions,
    fieldArray,
    disabledCostCenterOptions,
    onAddCostCenterRow,
    getCostCenterName,
    recalculateTotal,
    customFields,
    onListUpdate,
  };
};
